import type { LabelGroupId } from 'features/timeSeries/labels/labelGroups'
import type { ProcessDataLabel } from 'features/timeSeries/labels/processDataLabelsService'

/** A group chip's value: a real group, or the bucket for labels without one. */
export type LabelGroupFilterValue = LabelGroupId | 'ungrouped'

export interface LabelFilterState {
  /** Free text over name, description, and pens (display name and raw id). */
  query: string
  /** Open-ended when null: `fromMs` alone means "ends after", and vice versa. */
  fromMs: number | null
  toMs: number | null
  /** A pen the label must scope. */
  sensorId: string | null
  /** Empty means every group, including ungrouped. */
  groupIds: LabelGroupFilterValue[]
}

export const EMPTY_LABEL_FILTERS: LabelFilterState = {
  query: '',
  fromMs: null,
  toMs: null,
  sensorId: null,
  groupIds: [],
}

export const hasActiveLabelFilters = (filters: LabelFilterState): boolean =>
  filters.query.trim() !== '' ||
  filters.fromMs !== null ||
  filters.toMs !== null ||
  filters.sensorId !== null ||
  filters.groupIds.length > 0

/**
 * A label matches a time filter when it OVERLAPS the window rather than sitting
 * inside it: an outage that starts before the window and runs into it is a hit,
 * which is what someone scanning a period expects to find.
 */
const overlapsRange = (
  label: ProcessDataLabel,
  fromMs: number | null,
  toMs: number | null
): boolean =>
  (fromMs === null || label.endMs >= fromMs) &&
  (toMs === null || label.startMs <= toMs)

const matchesQuery = (
  label: ProcessDataLabel,
  query: string,
  penNameFor: (sensorId: string) => string
): boolean => {
  const needle = query.trim().toLowerCase()
  if (!needle) return true

  const haystacks = [
    label.name,
    label.description,
    ...label.sensorIds,
    ...label.sensorIds.map(penNameFor),
  ]

  return haystacks.some((value) => value.toLowerCase().includes(needle))
}

export const filterLabels = (
  labels: ProcessDataLabel[],
  filters: LabelFilterState,
  penNameFor: (sensorId: string) => string = (sensorId) => sensorId
): ProcessDataLabel[] =>
  labels.filter(
    (label) =>
      overlapsRange(label, filters.fromMs, filters.toMs) &&
      (filters.sensorId === null ||
        label.sensorIds.includes(filters.sensorId)) &&
      (filters.groupIds.length === 0 ||
        filters.groupIds.includes(label.groupId ?? 'ungrouped')) &&
      matchesQuery(label, filters.query, penNameFor)
  )

/** Every pen any label scopes, for the tag filter's options. */
export const collectLabelSensorIds = (labels: ProcessDataLabel[]): string[] => {
  const sensorIds = new Set<string>()
  labels.forEach((label) => label.sensorIds.forEach((id) => sensorIds.add(id)))
  return [...sensorIds]
}
